// assets/product-sticky.js
(() => {
  const TAG = 'product-sticky';
  if (customElements.get(TAG)) return;

  class ProductSticky extends HTMLElement {
    connectedCallback() {
      // 1) Find the canonical product form anywhere on the page
      this.form = document.querySelector('[data-product-form]');
      if (!this.form) return;

      // 2) Cache important nodes (wider fallbacks for new themes)
      this.atcBtn       = this.form.querySelector('[data-add-to-cart]');
      this.priceWrap    =
        this.form.querySelector('[data-price-wrapper]') ||
        document.querySelector('[data-price-wrapper]') ||
        document.querySelector('.product__price,[data-product-price]') || null;

      this.variantInput = this.form.querySelector('input[name="id"][data-product-select]');
      this.submitHolder =
        this.form.querySelector('.product__submit__holder') ||
        this.form;

      // 3) Build & inject bar once
      this.bar = this._buildBar();
      document.body.appendChild(this.bar);

      // 4) Show bar only when the native ATC is off-screen
      this.io = new IntersectionObserver((entries) => {
        const inView = entries[0].isIntersecting;
        this.bar.classList.toggle('is-visible', !inView);
      }, { threshold: 0 });
      this.io.observe(this.submitHolder);

      // 5) Initial sync + bindings
      this._sync();
      this._bind();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.mo?.disconnect();
      this.bar?.remove();
    }

    _bind() {
      // Mirror price changes even if theme re-renders price outside the form
      if (this.priceWrap) {
        this.mo = new MutationObserver(() => this._sync());
        this.mo.observe(this.priceWrap, { childList: true, subtree: true, characterData: true });
      }

      // Refresh on option/qty changes
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-single-option-selector], [name="quantity"], select[name^="options["]')) {
          this._sync();
        }
      });

      // Click sticky ATC -> trigger original button (preserves theme logic)
      this.bar.querySelector('[data-sticky-atc]').addEventListener('click', (e) => {
        e.preventDefault();
        if (this.atcBtn) {
          this.atcBtn.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        } else {
          this.form.requestSubmit?.();
        }
      });

      // Section reloads / Theme editor
      document.addEventListener('shopify:section:load', () => this._sync(), { passive: true });
    }

    _sync() {
      // ---- TITLE (fixed to avoid Stamped review badges) ----
      const titleEl =
        document.querySelector('h1.product__title, .product__title, .product-title') ||
        document.querySelector('[data-product-title]:not(.stamped-product-reviews-badge)');
      const title = (titleEl?.textContent || '').trim();
      this.bar.querySelector('[data-sticky-title]').textContent = title;

      // Image (broader gallery selectors)
      const galleryImg = document.querySelector(
        '.product-gallery__media img, [data-product-single-media-wrapper] img, .product__media img, .product-media img'
      );
      const imgEl = this.bar.querySelector('[data-sticky-img]');
      if (galleryImg && imgEl && !imgEl.dataset.bound) {
        imgEl.src = galleryImg.currentSrc || galleryImg.src || '';
        imgEl.alt = galleryImg.alt || title || 'Product image';
        imgEl.dataset.bound = '1';
      }

      // Variant selections summary
      const parts = [];
      this.form.querySelectorAll('.radio__fieldset input[type="radio"]:checked').forEach((el) => {
        const name = (el.name || '').replace(/^options\[|\]$/g, '');
        parts.push(`${name}: ${el.value}`);
      });
      this.form.querySelectorAll('input[data-popout-input][data-single-option-selector], select[name^="options["]').forEach((el) => {
        const name = (el.name || '').replace(/^options\[|\]$/g, '');
        const val = el.value;
        if (name && val && !parts.find(p => p.startsWith(name + ':'))) parts.push(`${name}: ${val}`);
      });
      this.bar.querySelector('[data-sticky-selected]').textContent = parts.join(' · ');

      // Price + compare (works even if price lives outside form)
      const priceRoot = this.priceWrap || document;
      const priceText   = priceRoot.querySelector('[data-product-price], .product__price [data-product-price], .product__price .product_price--regular, .product__price')?.textContent?.trim() || '';
      const compareText = priceRoot.querySelector('[data-compare-price], .product_price--compare, s[data-compare-price]')?.textContent?.trim() || '';

      this.bar.querySelector('[data-sticky-price]').textContent = priceText;
      const compareEl = this.bar.querySelector('[data-sticky-compare]');
      if (compareText) {
        compareEl.textContent = compareText;
        compareEl.classList.remove('hidden');
      } else {
        compareEl.classList.add('hidden');
      }

      // Availability
      const disabled = this.atcBtn?.disabled || /sold\s*out/i.test(this.atcBtn?.innerText || '');
      this.bar.querySelector('[data-sticky-atc]').disabled = !!disabled;

      // Mirror button label
      const stickyText =
        this.atcBtn?.querySelector('[data-add-to-cart-text]')?.textContent?.trim()
        || this.atcBtn?.textContent?.trim()
        || 'Add to cart';
      this.bar.querySelector('[data-sticky-atc-text]').textContent = stickyText;
    }

    _buildBar() {
      const el = document.createElement('div');
      el.className = 'sticky-atc';
      el.innerHTML = `
        <div class="sticky-atc__inner">
          <div class="sticky-atc__left">
            <img class="sticky-atc__img" data-sticky-img alt="">
            <div class="sticky-atc__meta">
              <div class="sticky-atc__title" data-sticky-title></div>
              <div class="sticky-atc__selected" data-sticky-selected></div>
            </div>
          </div>
          <div class="sticky-atc__right">
            <div class="sticky-atc__price">
              <span class="sticky-atc__price--current" data-sticky-price></span>
              <s class="sticky-atc__price--compare hidden" data-sticky-compare></s>
            </div>
            <button type="button" class="btn btn--advanced product__submit__add" data-sticky-atc>
              <hover-button></hover-button>
              <span data-sticky-atc-text>Add to cart</span>
            </button>
          </div>
        </div>
      `;
      return el;
    }
  }

  customElements.define(TAG, ProductSticky);

  // --- Self-install so you don't need to add <product-sticky> to the theme ---
  const ensureInstance = () => {
    if (!document.querySelector('[data-product-form]')) return; // not a product page
    if (!document.querySelector(TAG)) {
      const host = document.createElement(TAG);
      document.body.appendChild(host);
    }
  };
  // Run now and on section loads
  document.addEventListener('DOMContentLoaded', ensureInstance, { once: true });
  document.addEventListener('shopify:section:load', ensureInstance);
})();
